/**
 * One metric group drawn over time — a card on the Charts tab (DOP-609).
 *
 * Charts were "Show trends", a button that swapped the grid out; on the Summit
 * call the analyst loved them and had barely found them. Now they are a tab of
 * their own, and each card leads with the number a reader looks for first: the
 * group's headline metric in the latest period, and how it moved since the one
 * before.
 *
 * The same numbers as the Table, read a second way. Hovering a point shows its
 * values; the evidence behind a number lives on the Table, not here.
 *
 * Only the metrics sharing the group's most common unit are plotted, and the
 * ones left out are named underneath. Dollars and percentages on one axis is a
 * chart that lies about magnitude; dropping series silently would be worse.
 *
 * `mixedUnits` is for a chart that is two units ON PURPOSE (NOI margin beside
 * DSCR): every metric is plotted, each unit on its own axis — left, then right.
 */

import ReactEChartsCore from "echarts-for-react/lib/core";
import { LineChart } from "echarts/charts";
import { GridComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import React, { useMemo } from "react";

import type { KpiMetricGroup, KpiMetricRow, KpiUnit } from "../../types/kpi";
import { formatKpiValue, formatPeriod } from "./formatKpiValue";
import { type Headline, headlineOf, valueAt } from "./kpiChartHeadline";

echarts.use([LineChart, GridComponent, TooltipComponent, CanvasRenderer]);

/*
 * Categorical series colors, varying in hue first so adjacent lines stay apart
 * under a red-green deficiency. The brand's dark teal leads.
 */
export const LINE_COLORS = ["#004C4B", "#C2410C", "#3538CD", "#5B6472", "#9333EA"];

const AXIS_LABEL_COLOR = "#8a9099";

/** Module constant: an inline object literal would remount the chart. */
const CHART_STYLE = { height: 180 };

export interface KpiChartCardProps {
	group: KpiMetricGroup;
	periods: string[];
	/** The table's grain — the x axis labels the same buckets the grid does. */
	grain?: string;
	/** Plot every metric, one axis per unit, instead of the dominant unit only. */
	mixedUnits?: boolean;
}

function dominantUnit(group: KpiMetricGroup): KpiUnit {
	const counts = new Map<KpiUnit, number>();
	group.metrics.forEach((metric) => {
		counts.set(metric.unit, (counts.get(metric.unit) ?? 0) + 1);
	});
	let best: KpiUnit = group.metrics[0]?.unit ?? "usd";
	let bestCount = -1;
	counts.forEach((count, unit) => {
		if (count > bestCount) {
			best = unit;
			bestCount = count;
		}
	});
	return best;
}

/**
 * The change since the period before, in words a reader uses: a percentage for
 * money and counts, points for a percentage (a rate moving 94.7% → 94.9% is
 * "+0.2 pts", not "+0.2%").
 */
function changeText(headline: Headline, grain: string): string | null {
	const { value, previous, previousPeriod, metric } = headline;
	if (previous === null || previousPeriod === null) return null;
	const label = formatPeriod(previousPeriod, grain);
	if (metric.unit === "pct") {
		// Percentages arrive as fractions (0.949), so points are the difference × 100.
		const points = (value - previous) * 100;
		return `${points >= 0 ? "+" : ""}${points.toFixed(1)} pts vs ${label}`;
	}
	if (previous === 0) return null;
	const change = ((value - previous) / Math.abs(previous)) * 100;
	return `${change >= 0 ? "+" : ""}${change.toFixed(1)}% vs ${label}`;
}

function escapeHtml(text: string): string {
	return text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
}

const KpiChartCard: React.FC<KpiChartCardProps> = ({
	group,
	periods,
	grain = "month",
	mixedUnits = false,
}) => {
	// One axis per unit, left then right. A chart has two sides, so a third
	// unit is left out and named like any other unplotted metric.
	const axisUnits = useMemo<KpiUnit[]>(() => {
		if (!mixedUnits) return [dominantUnit(group)];
		const units: KpiUnit[] = [];
		group.metrics.forEach((metric) => {
			if (!units.includes(metric.unit)) units.push(metric.unit);
		});
		return units.slice(0, 2);
	}, [group, mixedUnits]);
	const plotted = useMemo(
		() => group.metrics.filter((metric) => axisUnits.includes(metric.unit)),
		[group, axisUnits],
	);
	const omitted = useMemo(
		() => group.metrics.filter((metric) => !axisUnits.includes(metric.unit)),
		[group, axisUnits],
	);
	const headline = useMemo(() => headlineOf(plotted, periods), [plotted, periods]);
	const labels = useMemo(
		() => periods.map((period) => formatPeriod(period, grain)),
		[periods, grain],
	);

	const option = useMemo(
		() => ({
			grid: {
				left: 56,
				right: axisUnits.length > 1 ? 48 : 12,
				top: 12,
				bottom: 28,
			},
			tooltip: {
				trigger: "axis" as const,
				backgroundColor: "#111827",
				borderWidth: 0,
				padding: [9, 11],
				textStyle: { color: "#ffffff", fontSize: 13 },
				extraCssText:
					"border-radius:8px;box-shadow:0 12px 28px rgba(17,24,39,0.28);",
				axisPointer: {
					type: "line" as const,
					lineStyle: { color: "#c9ccd1", type: "dashed" as const },
				},
				formatter: (params: unknown) => {
					const items = Array.isArray(params) ? params : [params];
					const first = items[0] as { dataIndex?: number } | undefined;
					const period = labels[first?.dataIndex ?? 0] ?? "";
					const rows = items
						.map((raw) => {
							const item = raw as {
								seriesName?: string;
								seriesIndex?: number;
								value?: unknown;
							};
							const value =
								typeof item.value === "number" ? item.value : null;
							const unit =
								plotted[item.seriesIndex ?? 0]?.unit ?? axisUnits[0];
							return `<div style="font-weight:600">${escapeHtml(
								item.seriesName ?? "",
							)} ${escapeHtml(formatKpiValue(value, unit))}</div>`;
						})
						.join("");
					return `<div style="font-size:12px;color:#d1d5db">${escapeHtml(
						period,
					)}</div>${rows}`;
				},
			},
			xAxis: {
				type: "category" as const,
				data: labels,
				axisLabel: { fontSize: 10, color: AXIS_LABEL_COLOR },
				axisLine: { lineStyle: { color: "#e5e7eb" } },
				axisTick: { show: false },
			},
			yAxis: axisUnits.map((unit, axisIndex) => {
				const onAxis = plotted
					.map((metric, index) => ({ metric, index }))
					.filter(({ metric }) => metric.unit === unit);
				return {
					type: "value" as const,
					position: axisIndex === 0 ? ("left" as const) : ("right" as const),
					/*
					 * Fit the axis to the data instead of anchoring it at zero: a rate
					 * moving 92.9% → 95.3% drew a flat line across 0–100%. These are
					 * lines, where position over time is what gets read.
					 */
					scale: true,
					axisLabel: {
						fontSize: 10,
						// With two axes, an axis that carries one line wears its
						// color: that is what says which scale a line is read on.
						color:
							axisUnits.length > 1 && onAxis.length === 1
								? LINE_COLORS[onAxis[0].index % LINE_COLORS.length]
								: AXIS_LABEL_COLOR,
						formatter: (value: number) => formatKpiValue(value, unit),
					},
					// One set of gridlines: the second axis's would not line up.
					splitLine: {
						show: axisIndex === 0,
						lineStyle: { color: "#eef0f2" },
					},
				};
			}),
			series: plotted.map((metric, index) => ({
				name: metric.label,
				type: "line" as const,
				yAxisIndex: axisUnits.indexOf(metric.unit),
				smooth: false,
				symbol: "circle",
				symbolSize: 7,
				// Gaps stay gaps: connecting across a period with no value would
				// draw a trend the data does not have.
				connectNulls: false,
				lineStyle: { width: 2.25, color: LINE_COLORS[index % LINE_COLORS.length] },
				itemStyle: {
					color: "#ffffff",
					borderColor: LINE_COLORS[index % LINE_COLORS.length],
					borderWidth: 2,
				},
				emphasis: { scale: 1.4 },
				cursor: "default",
				data: periods.map((period) => valueAt(metric, period)),
			})),
		}),
		[plotted, periods, axisUnits, labels],
	);


	if (plotted.length === 0) return null;

	const change = headline ? changeText(headline, grain) : null;

	return (
		<section
			aria-label={group.label}
			className="flex flex-col gap-1.5 rounded-xl border border-gray-200 bg-white px-4 pb-2.5 pt-3.5"
		>
			<div className="flex items-start gap-2.5">
				<div className="flex min-w-0 flex-col gap-0.5">
					<h3 className="text-sm font-semibold text-text-strong">{group.label}</h3>
					{headline && (
						<div className="flex flex-wrap items-baseline gap-x-2">
							<span className="text-[22px] font-bold tabular-nums text-[#111111]">
								{formatKpiValue(headline.value, headline.metric.unit)}
							</span>
							<span className="text-xs text-text-subtle">
								{plotted.length > 1 ? `${headline.metric.label}, ` : ""}
								{formatPeriod(headline.period, grain)}
							</span>
							{change && (
								<span className="text-xs font-semibold text-text-muted">{change}</span>
							)}
						</div>
					)}
				</div>
			</div>
			{plotted.length > 1 && (
				<div className="flex flex-wrap gap-x-3.5 gap-y-1 text-xs text-text-muted">
					{plotted.map((metric, index) => (
						<span key={metric.key} className="flex items-center gap-1.5">
							<span
								aria-hidden="true"
								className="h-[3px] w-3.5 rounded-sm"
								style={{ background: LINE_COLORS[index % LINE_COLORS.length] }}
							/>
							{metric.label}
						</span>
					))}
				</div>
			)}
			<ReactEChartsCore
				echarts={echarts}
				option={option}
				style={CHART_STYLE}
				notMerge
				lazyUpdate
			/>
			{omitted.length > 0 && (
				<p className="text-xs text-text-muted">
					Not plotted (different unit): {omitted.map((m) => m.label).join(", ")}
				</p>
			)}
		</section>
	);
};

export default KpiChartCard;
